import { Box, Flex, Group, Stack, Text } from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import "@mantine/charts/styles.css";
import type { GuestNetworkWhoAmIStat } from "../../hooks/useGuestNetworkStats";
import {
  GUEST_NETWORK_STATS_EMPTY,
  GUEST_NETWORK_STATS_UNAVAILABLE,
} from "../../hooks/useGuestNetworkStats";
import { useDiscoverHeroDonutMetrics, type DiscoverHeroStatLayout } from "../../hooks/useDiscoverHeroDonutMetrics";

const NAVY = "#04305c";

export const WHO_AM_I_DONUT_COLORS = [
  "#04305c",
  "#3B82F6",
  "#22C55E",
  "#F97316",
  "#F59E0B",
  "#7C3AED",
];

type DiscoverHeroWhoAmIDonutProps = {
  segments: GuestNetworkWhoAmIStat[];
  loadError?: boolean;
  layout?: DiscoverHeroStatLayout;
};

export default function DiscoverHeroWhoAmIDonut({
  segments,
  loadError = false,
  layout = "row",
}: DiscoverHeroWhoAmIDonutProps) {
  const donut = useDiscoverHeroDonutMetrics();
  const isColumn = layout === "column";
  const chartSize = isColumn ? 96 : donut.size;
  const visibleSegments = segments.filter((segment) => segment.count > 0 && segment.percent > 0);
  const chartData = visibleSegments.map((segment) => {
    const index = segments.findIndex((row) => row.key === segment.key);
    return {
      name: segment.label,
      value: segment.count,
      color: WHO_AM_I_DONUT_COLORS[index % WHO_AM_I_DONUT_COLORS.length],
    };
  });

  const legend = (
    <Stack gap={donut.legendGap} style={{ flex: "1 1 0", minWidth: 0, overflowY: "auto" }}>
      {visibleSegments.map((segment) => {
        const index = segments.findIndex((row) => row.key === segment.key);
        return (
        <Group key={segment.key} justify="space-between" wrap="nowrap" gap="xs">
          <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
            <Box
              w={donut.swatch}
              h={donut.swatch}
              style={{
                borderRadius: 999,
                flexShrink: 0,
                backgroundColor: WHO_AM_I_DONUT_COLORS[index % WHO_AM_I_DONUT_COLORS.length],
              }}
            />
            <Text size={donut.legendSize} c="#334155" lh={1.3} style={{ minWidth: 0 }}>
              {segment.label}
            </Text>
          </Group>
          <Text size={donut.legendSize} fw={700} c={NAVY} pl={6} style={{ flexShrink: 0 }}>
            {segment.percentLabel ?? `${segment.percent}%`}
          </Text>
        </Group>
        );
      })}
    </Stack>
  );

  const chartBlock = (
    <Stack gap={0} align="center" style={{ flex: isColumn ? undefined : "0 0 auto", flexShrink: 0 }}>
      <DonutChart
        data={chartData}
        withLabelsLine={false}
        withTooltip
        tooltipDataSource="segment"
        size={chartSize}
        thickness={donut.thickness}
        valueFormatter={(value) => value.toLocaleString()}
        styles={{
          label: {
            fontSize: donut.labelFontSize,
            fontWeight: 800,
            fill: NAVY,
          },
        }}
      />
      <Text size={donut.companiesSize} fw={700} c={NAVY}>
        Companies
      </Text>
    </Stack>
  );

  return (
    <Stack gap={6} h="100%" justify="center" style={{ flex: "1 1 auto", minHeight: 0 }}>
      {chartData.length > 0 ? (
        isColumn ? (
          <Stack gap={8} align="center" w="100%" style={{ flex: "0 0 auto" }}>
            {chartBlock}
            <Box w="100%">{legend}</Box>
          </Stack>
        ) : (
          <Flex
            align="center"
            gap={8}
            wrap="nowrap"
            style={{ flex: "0 0 auto", minWidth: 0, minHeight: 0 }}
          >
            {chartBlock}
            {legend}
          </Flex>
        )
      ) : (
        <Text size="sm" c="dimmed" py="md" ta="center">
          {loadError ? GUEST_NETWORK_STATS_UNAVAILABLE : GUEST_NETWORK_STATS_EMPTY}
        </Text>
      )}

      {chartData.length === 0 && !loadError ? legend : null}
    </Stack>
  );
}
